import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useUser, useSignIn } from '@clerk/react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

function WavingShippy({ size = 96 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width={size} height={size}>
      <defs>
        <linearGradient id="wt" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#93C5FD" /><stop offset="100%" stopColor="#60A5FA" />
        </linearGradient>
        <linearGradient id="wf" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#3B82F6" /><stop offset="100%" stopColor="#2563EB" />
        </linearGradient>
        <linearGradient id="ws" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#60A5FA" /><stop offset="100%" stopColor="#3B82F6" />
        </linearGradient>
      </defs>
      {/* Legs */}
      <rect x="30" y="72" width="8" height="16" rx="4" fill="#1E40AF" />
      <rect x="54" y="72" width="8" height="16" rx="4" fill="#1E40AF" />
      <ellipse cx="34" cy="90" rx="8" ry="4" fill="#1D4ED8" />
      <ellipse cx="58" cy="90" rx="8" ry="4" fill="#1D4ED8" />
      {/* Body group — gentle bounce */}
      <g>
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-2;0,0" dur="1.2s" repeatCount="indefinite" />
        {/* Left arm waving */}
        <g>
          <animateTransform attributeName="transform" type="rotate" values="0,20,48;-30,20,48;0,20,48" dur="0.8s" repeatCount="indefinite" />
          <path d="M20 48 Q10 38, 8 26 Q7 20, 12 18" fill="none" stroke="#1E40AF" strokeWidth="7" strokeLinecap="round" />
          <circle cx="12" cy="17" r="5" fill="#FCD34D" />
        </g>
        {/* Right arm */}
        <path d="M72 48 Q82 52, 80 62" fill="none" stroke="#1E40AF" strokeWidth="7" strokeLinecap="round" />
        <circle cx="80" cy="63" r="5" fill="#FCD34D" />
        {/* Box body */}
        <path d="M22 30 L46 18 L76 30 L52 42 Z" fill="url(#wt)" stroke="#1E40AF" strokeWidth="1.5" />
        <line x1="46" y1="18" x2="52" y2="42" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" opacity="0.8" />
        <path d="M22 30 L52 42 L52 75 L22 63 Z" fill="url(#wf)" stroke="#1E40AF" strokeWidth="1.5" />
        <path d="M76 30 L52 42 L52 75 L76 63 Z" fill="url(#ws)" stroke="#1E40AF" strokeWidth="1.5" />
        <line x1="37" y1="36" x2="37" y2="69" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" opacity="0.8" />
        {/* Eyes */}
        <ellipse cx="33" cy="48" rx="3.5" ry="4" fill="white" />
        <ellipse cx="45" cy="46" rx="3.5" ry="4" fill="white" />
        <ellipse cx="34.5" cy="47.5" rx="2" ry="2.5" fill="#1E293B" />
        <ellipse cx="46.5" cy="45.5" rx="2" ry="2.5" fill="#1E293B" />
        <circle cx="35.5" cy="46" r="1" fill="white" />
        <circle cx="47.5" cy="44" r="1" fill="white" />
        {/* Smile */}
        <path d="M30 54 Q38 62, 48 54" fill="none" stroke="#1E293B" strokeWidth="2.5" strokeLinecap="round" />
        {/* Cheeks */}
        <ellipse cx="28" cy="55" rx="3" ry="2" fill="#F9A8D4" opacity="0.5" />
        <ellipse cx="50" cy="53" rx="3" ry="2" fill="#F9A8D4" opacity="0.5" />
      </g>
    </svg>
  );
}

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: 10 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Login = () => {
  const { isSignedIn } = useUser();
  const { signIn } = useSignIn();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (isSignedIn) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      await signIn.sso({
        strategy: 'oauth_google',
        redirectCallbackUrl: '/sso-callback',
        redirectUrl: '/dashboard',
      });
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError('Sign-in failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-lg border border-border bg-card">
        <CardHeader className="space-y-2 text-center">
          <div className="flex justify-center">
            <WavingShippy size={96} />
          </div>
          <CardTitle>Welcome back</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <Button
            onClick={handleGoogleSignIn}
            className="w-full"
            disabled={loading || !signIn}
            variant="outline"
          >
            {loading ? (
              'Signing in...'
            ) : (
              <>
                <GoogleIcon />
                Sign in with Google
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
